import type { SelectableOptionProps } from '@assembly-web/ui';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { trackDiscoverAction } from '../../services/analytics';
import { useInvalidateFeed } from '../useInvalidateFeed';

const initialSearchValues = {
  flowsSearchValue: '',
  collectionsSearchValue: '',
  postedBySearchValue: '',
  mentionedBySearchValue: '',
  assigneeSearchValue: '',
  reportsToSearchValue: '',
};

// TODO: move this to secondaryFiltersStore
export function useSecondaryFilterState() {
  const [params, setSearchParams] = useSearchParams();

  const [searchValues, setSearchValues] = useState(initialSearchValues);

  const { invalidateFeed } = useInvalidateFeed();

  const handleClearClick = (filtersToReset: string[]) => {
    invalidateFeed();
    const updatedParams = new URLSearchParams(params);
    filtersToReset.forEach((filter) => {
      if (updatedParams.has(filter)) {
        trackDiscoverAction('secondaryFilterCleared', {
          secondaryFilterType: filter,
        });
        updatedParams.delete(filter);
      }
    });
    setSearchParams(updatedParams);
  };

  const resetSearchFilters = useCallback(
    () => setSearchValues(initialSearchValues),
    []
  );

  const handleDoneClick = useCallback(
    ({
      label,
      currentSelectedOptions,
      labelToKeyMap,
    }: {
      label: string;
      currentSelectedOptions: SelectableOptionProps[];
      labelToKeyMap: Record<string, string>;
    }) => {
      const selectedOptionsIds = currentSelectedOptions
        .map((option) => option.id)
        .join(',');

      const filter = labelToKeyMap[label];

      resetSearchFilters();

      if (filter) {
        if (selectedOptionsIds.length) {
          const trackEventProps = {
            secondaryFilterType: filter,
            selectedValues: currentSelectedOptions.map(
              (option) => option.value as string
            ),
          };
          trackDiscoverAction('secondaryFilterApplied', trackEventProps);
          setSearchParams({
            ...Object.fromEntries(params),
            [filter]: selectedOptionsIds,
          });
        } else {
          const updatedParams = new URLSearchParams(params);
          if (updatedParams.has(filter)) {
            updatedParams.delete(filter);
          }
          setSearchParams(updatedParams);
        }
      }
    },
    [params, resetSearchFilters, setSearchParams]
  );

  const handleSearchChange = useCallback(
    ({
      label,
      value,
      labelToKeyMap,
    }: {
      label: string;
      value: string;
      labelToKeyMap: Record<string, string>;
    }) => {
      if (labelToKeyMap[label]) {
        const keyToUpdate = labelToKeyMap[label];
        setSearchValues({
          ...searchValues,
          [keyToUpdate]: value,
        });
      }
    },
    [searchValues]
  );

  return {
    searchValues,
    handleDoneClick,
    handleClearClick,
    handleSearchChange,
    resetSearchFilters,
  };
}
